import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  AttendanceStatistics,
  FloorsApiService,
  MeetingRoomApiService,
  ProcessedStatusesMeetingRoom,
  RetrievePlan,
  useService,
  AbilityContext,
} from '@innowise-group/core';
import {RESIZE_OBSERVER_DESKTOP_SCREEN_SIZE, useResizeObserver} from '@innowise-group/ui-kit';
import {reloadPage} from '@innowise-group/utilities';

import {FloorElements} from '../../services/floors-data-manager/floors-data-manager.types';
import {FloorsModalsFacadeService} from '../../services/floors-modals-facade';
import {mapRoomsToElements, mapViewToElements, mapWorkspaceToElements} from '../../utilities/element-mapper.utility';
import {forkJoin} from 'rxjs';

export interface FloorData {
  country: string;
  address: string;
  floor: string;
  isVirtual: boolean;
  fullAddress: string;
}

export interface UsePlanDetailsResult {
  floor: FloorData;
  isPlanLoading: boolean;
  setIsPlanLoading: (value: boolean) => void;
  isPlanExists: boolean;
  isAttendanceLoading: boolean;
  uploadPlanHandler: () => void;
  refreshAttendanceStatistics: () => void;
}

export const usePlanDetails = (
  floorId: string,
  initialize: (elements: FloorElements) => void,
  isViewMode: boolean,
  setAttendanceStatistics?: React.Dispatch<React.SetStateAction<AttendanceStatistics>>,
): UsePlanDetailsResult => {
  const [floor, setFloor] = useState<FloorData>({} as FloorData);
  const [isPlanLoading, setIsPlanLoading] = useState(true);
  const [isPlanExists, setIsPlanExists] = useState<boolean>(false);
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(Boolean(setAttendanceStatistics));
  const floorsApi = useService(FloorsApiService);
  const meetingRoomApi = useService(MeetingRoomApiService);
  const floorsModalsFacade = useService(FloorsModalsFacadeService);
  const {t} = useTranslation();
  const isDesktopView = useResizeObserver('application-container', RESIZE_OBSERVER_DESKTOP_SCREEN_SIZE);
  const ability = useContext(AbilityContext);

  // Keep mode/desktop flags in refs so toggle edit does not re-fetch the entire floor plan.
  // View-mode is applied on existing nodes via useFloorDetails' isViewMode effect instead.
  const isViewModeRef = useRef(isViewMode);
  const isDesktopViewRef = useRef(isDesktopView);
  isViewModeRef.current = isViewMode;
  isDesktopViewRef.current = isDesktopView;
  const hasAttendanceLoadedRef = useRef(false);

  const mapPlanToElements = useCallback(
    (elements: Omit<RetrievePlan, 'location' | 'floor'>, meetingRoomStatuses: ProcessedStatusesMeetingRoom) => {
      const viewMode = isViewModeRef.current;
      const desktopView = isDesktopViewRef.current;
      const floorElements = {} as FloorElements;
      Object.keys(elements).forEach((key) => {
        if (key === 'workspaces') {
          floorElements[key] = mapWorkspaceToElements(elements[key], viewMode);
        } else if (key === 'rooms') {
          floorElements[key] = mapRoomsToElements(elements[key], viewMode, desktopView, meetingRoomStatuses);
        } else if (key === 'doors') {
          floorElements[key] = mapViewToElements(elements[key], {
            selectable: !viewMode,
            isViewMode: viewMode,
          });
        } else {
          floorElements[key] = mapViewToElements(elements[key]);
        }
      });
      initialize(floorElements);
    },
    [initialize],
  );
  const getFloorPlanById = useCallback(
    (targetFloorId: string) => {
      return forkJoin([
        floorsApi.retrievePlan(targetFloorId),
        meetingRoomApi.getMeetingRoomStatuses(targetFloorId),
      ]).subscribe({
        next([retrievePlanData, meetingRoomStatuses]) {
          const {floor, location, ...rest} = retrievePlanData;
          const {office, id, isVirtual, officeCity, officeCountry, officeAddress, number} = floor;
          const floorData: FloorData = {
            country: location,
            address: office,
            floor: id,
            isVirtual,
            fullAddress: `${officeCountry}, ${officeCity}, ${officeAddress}, ${t('pages.floors.floor')} ${number}`,
          };
          setFloor(floorData);
          mapPlanToElements(rest, meetingRoomStatuses);
        },
        error() {
          setIsPlanExists(false);
          setIsPlanLoading(false);
        },
        complete() {
          setIsPlanExists(true);
          setIsPlanLoading(false);
        },
      });
    },
    [floorsApi, meetingRoomApi, mapPlanToElements, t],
  );

  const canReadAttendance =
    Boolean(ability?.can('READ', 'ROOM-ATTENDANCE')) || Boolean(ability?.can('READ', 'FLOOR-ATTENDANCE'));

  // Coalesce concurrent mutation-driven refreshes: keep only the latest subscription.
  const attendanceRequestRef = useRef<{unsubscribe: () => void} | null>(null);

  const refreshAttendanceStatistics = useCallback(() => {
    if (!setAttendanceStatistics || !canReadAttendance || !floorId) {
      return;
    }

    if (!hasAttendanceLoadedRef.current) {
      setIsAttendanceLoading(true);
    }
    attendanceRequestRef.current?.unsubscribe();
    attendanceRequestRef.current = floorsApi.getFloorAttendanceStatistics(floorId).subscribe({
      next(data) {
        setAttendanceStatistics({
          attendance_percentage: data.attendance_percentage ?? 0,
          rooms: data.rooms,
          total_workspaces: data.total_workspaces,
          free_workspaces: data.free_workspaces ?? 0,
          rooms_count: data.rooms_count,
        });
        hasAttendanceLoadedRef.current = true;
        setIsAttendanceLoading(false);
      },
      error() {
        setIsAttendanceLoading(false);
      },
      complete() {},
    });
  }, [canReadAttendance, floorId, floorsApi, setAttendanceStatistics]);

  const fetchPlan = useCallback(() => {
    return getFloorPlanById(floorId);
  }, [getFloorPlanById, floorId]);

  useEffect(() => {
    hasAttendanceLoadedRef.current = false;
    if (setAttendanceStatistics && canReadAttendance) {
      setIsAttendanceLoading(true);
    }
  }, [floorId, setAttendanceStatistics, canReadAttendance]);

  useEffect(() => {
    const subscriber = fetchPlan();
    return () => {
      subscriber.unsubscribe();
    };
  }, [fetchPlan]);

  useEffect(() => {
    if (!canReadAttendance) {
      setIsAttendanceLoading(false);
      return;
    }

    refreshAttendanceStatistics();

    return () => {
      attendanceRequestRef.current?.unsubscribe();
      attendanceRequestRef.current = null;
    };
  }, [canReadAttendance, refreshAttendanceStatistics]);

  const openUploadModal = useCallback(
    (toSaveArrangements = true) => {
      floorsModalsFacade.openPlanUploadModal((values) => {
        setIsPlanLoading(true);
        floorsApi.uploadPlan(floorId, values, toSaveArrangements).subscribe(() => {
          fetchPlan();
          reloadPage();
        });
      }, t('modals.uploadPlan'));
    },
    [fetchPlan, floorId, floorsApi, floorsModalsFacade, t],
  );

  const handleUploadPlan = useCallback(() => {
    if (isPlanExists) {
      floorsModalsFacade
        .openUploadConfirmationModal(
          t('modals.uploadPlan'),
          t('modals.uploadPlanText'),
          t('buttons.upload'),
          t('buttons.cancel'),
        )
        .subscribe((toSaveArrangements) => openUploadModal(toSaveArrangements));
    } else {
      openUploadModal();
    }
  }, [floorsModalsFacade, isPlanExists, openUploadModal, t]);

  return {
    floor,
    isPlanLoading,
    setIsPlanLoading,
    isPlanExists,
    isAttendanceLoading,
    uploadPlanHandler: handleUploadPlan,
    refreshAttendanceStatistics,
  };
};
